// src/pages/DevTickets.js — Dev Queue list (redesign 2026-10-23).
// Laid out exactly like the Tickets list (head, stat cards, toolbar, table) so the two
// read the same way. A row opens the dev conversation page (DevTicketDetail.js);
// inside the desktop app's embed the links stay under /embed.
import { useState, useEffect, useCallback, useMemo, Fragment } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getAgents } from "../api";
import { Search, RefreshCw, Code, Zap, Pause, AlertCircle, CheckCircle2, Ticket as TicketIcon } from "lucide-react";
import { StatusBadge, PriorityBadge, MiniAvatar } from "../theme";
import useIsMobile from "../hooks/useIsMobile";

const API   = (process.env.REACT_APP_API_URL || "https://ticketing.midtc.com") + "/api";
const token = () => localStorage.getItem("midtc_token") || "";

const FILTERS_KEY = "midtc_dev_filters";
const DEFAULT_FILTERS = { status: "Active", priority: "", client: "", dev: "", search: "" };
function loadFilters() {
  try {
    const raw = JSON.parse(localStorage.getItem(FILTERS_KEY) || "null");
    if (!raw || typeof raw !== "object") return DEFAULT_FILTERS;
    const out = { ...DEFAULT_FILTERS };
    for (const k of Object.keys(DEFAULT_FILTERS)) if (typeof raw[k] === "string") out[k] = raw[k];
    return out;
  } catch { return DEFAULT_FILTERS; }
}

const ACTIVE = ["Open", "In Progress", "On Hold"];
// The queue reads top to bottom in the order work gets picked up (Juwan 2026-10-27): what is being
// worked on, then what is waiting, then what is parked, then what is done — each under its own
// heading. Inside a group: most urgent first, and among equals whoever has waited longest (the
// Waiting column). Done groups put the most recently finished first.
const STATUS_ORDER = { "In Progress": 0, "Open": 1, "On Hold": 2, "Resolved": 3, "Closed": 4 };
const PRIORITY_ORDER = { Critical: 0, High: 1, Medium: 2, Low: 3 };
const time = (d) => new Date(d || 0).getTime();
function queueOrder(a, b) {
  const s = (STATUS_ORDER[a.status] ?? 5) - (STATUS_ORDER[b.status] ?? 5);
  if (s) return s;
  if (["Resolved", "Closed"].includes(a.status)) return time(b.updated_at) - time(a.updated_at);
  return (PRIORITY_ORDER[a.priority] ?? 4) - (PRIORITY_ORDER[b.priority] ?? 4) || time(a.updated_at) - time(b.updated_at);
}
const groupHead = { padding: "9px 14px", fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
  color: "var(--t-text-muted)", background: "rgba(148,163,184,.07)", borderBottom: "1px solid var(--t-border-softer)" };
// The dev-tickets API returns the source ticket's raw number; show it as staff know it.
export const fmtTkt = (n) => (n == null || n === "" ? "" : /^\d+$/.test(String(n)) ? `TKT-${n}` : String(n));
const ago = (d) => {
  if (!d) return "—";
  const m = Math.floor((Date.now() - new Date(d).getTime()) / 60000);
  if (m < 60) return `${Math.max(1, m)}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
};

const inputStyle = {
  background: "var(--t-input)", border: "1px solid var(--t-border-mid)", borderRadius: 8,
  padding: "8px 12px", color: "var(--t-text-primary)", fontSize: 13, boxShadow: "var(--t-shadow-panel)", maxWidth: 230,
};

export default function DevTickets() {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const base = location.pathname.startsWith("/embed") ? "/embed/dev-tickets" : "/dev-tickets";
  const [tickets, setTickets] = useState([]);
  const [agents,  setAgents]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");
  const [filters, setFilters] = useState(loadFilters);

  useEffect(() => { try { localStorage.setItem(FILTERS_KEY, JSON.stringify(filters)); } catch { /* private mode */ } }, [filters]);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const res  = await fetch(`${API}/dev-tickets`, { headers: { Authorization: `Bearer ${token()}` } });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.error || `Could not load the Dev Queue (${res.status})`);
      setTickets(Array.isArray(data) ? data : []);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
  useEffect(() => { getAgents().then(r => setAgents(r.data || [])).catch(() => {}); }, []);

  const clients = useMemo(() => [...new Set(tickets.map(t => t.client_name).filter(Boolean))].sort(), [tickets]);
  const stats = useMemo(() => {
    const weekAgo = Date.now() - 7 * 86400000;
    return {
      open:       tickets.filter(t => t.status === "Open").length,
      inProgress: tickets.filter(t => t.status === "In Progress").length,
      onHold:     tickets.filter(t => t.status === "On Hold").length,
      noDev:      tickets.filter(t => ACTIVE.includes(t.status) && !(t.assigned_dev_details || []).length).length,
      resolved:   tickets.filter(t => ["Resolved", "Closed"].includes(t.status) && new Date(t.updated_at).getTime() > weekAgo).length,
    };
  }, [tickets]);
  const shown = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return tickets.filter(t =>
      (!filters.status || (filters.status === "Active" ? ACTIVE.includes(t.status) : t.status === filters.status)) &&
      (!filters.priority || t.priority === filters.priority) &&
      (!filters.client || t.client_name === filters.client) &&
      (!filters.dev || (filters.dev === "none" ? !(t.assigned_devs || []).length : (t.assigned_devs || []).includes(filters.dev))) &&
      (!q || [t.title, t.description, t.client_name, fmtTkt(t.ticket_number), t.source_subject, t.created_by_name].some(v => String(v || "").toLowerCase().includes(q))))
      .sort(queueOrder);
  }, [tickets, filters]);
  const groupSize = useMemo(() => shown.reduce((m, t) => ({ ...m, [t.status]: (m[t.status] || 0) + 1 }), {}), [shown]);

  const open = (e, id) => {
    if (e && (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1)) { window.open(`${base}/${id}`, "_blank", "noopener"); return; }
    navigate(`${base}/${id}`);
  };
  const devAgents = agents.filter(a => [a.role, ...(a.roles || [])].some(r => r === "dev" || r === "admin") && a.is_active !== false);
  const isFiltered = JSON.stringify(filters) !== JSON.stringify(DEFAULT_FILTERS);

  return (
    <div style={{ padding: isMobile ? 14 : 24, maxWidth: 1600, overflowY: "auto", height: "100%" }} className="fade-in">
      <div style={{ paddingBottom: 14, borderBottom: "1px solid var(--t-border-soft)", marginBottom: 16, display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: "var(--t-text-primary)", letterSpacing: "-0.5px" }}>Dev Queue</h1>
          <p style={{ color: "var(--t-text-faint)", fontSize: 13, marginTop: 3 }}>
            {stats.open + stats.inProgress + stats.onHold} open &nbsp;·&nbsp; {shown.length} shown &nbsp;·&nbsp; admins and developers only
          </p>
        </div>
        <span style={{ display: "inline-flex", alignItems: "center", padding: "4px 11px", borderRadius: 99, fontSize: 11, fontWeight: 700,
          background: "rgba(148,163,184,.12)", color: "var(--badge-gray)", border: "1px solid rgba(148,163,184,.32)" }}>Staff only</span>
      </div>

      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: isMobile ? "wrap" : undefined }}>
        {[
          { val: stats.open,       label: "Open",               color: "var(--badge-blue)",   Icon: Code,         f: { status: "Open" } },
          { val: stats.inProgress, label: "In Progress",        color: "var(--badge-amber)",  Icon: Zap,          f: { status: "In Progress" } },
          { val: stats.onHold,     label: "On Hold",            color: "var(--badge-violet)", Icon: Pause,        f: { status: "On Hold" } },
          { val: stats.noDev,      label: "No dev assigned",    color: "var(--badge-orange)", Icon: AlertCircle,  f: { status: "Active", dev: "none" } },
          { val: stats.resolved,   label: "Resolved this week", color: "var(--badge-green)",  Icon: CheckCircle2, f: { status: "Resolved" } },
        ].map(({ val, label, color, Icon, f }) => (
          <button key={label} type="button" onClick={() => setFilters({ ...DEFAULT_FILTERS, ...f })} style={{
            flex: isMobile ? "1 1 30%" : 1, textAlign: "left", background: "var(--t-gloss, none), var(--t-panel)",
            border: "1px solid var(--t-border-soft)", borderRadius: 12, padding: "13px 15px", position: "relative", overflow: "hidden",
            boxShadow: "var(--t-shadow-panel)", cursor: "pointer", fontFamily: "inherit",
          }} title={`Show: ${label}`}>
            <div style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", opacity: 0.12 }}><Icon size={26} style={{ color }} /></div>
            <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: -1, lineHeight: 1, color }}>{val}</div>
            <div style={{ fontSize: 10, color: "var(--t-text-faint)", marginTop: 4, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
          </button>
        ))}
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ flex: isMobile ? "1 1 100%" : 1, minWidth: isMobile ? 0 : 200, display: "flex", alignItems: "center", gap: 8,
          background: "var(--t-input)", border: "1px solid var(--t-border-mid)", borderRadius: 8, padding: "0 12px", boxShadow: "var(--t-shadow-panel)" }}>
          <Search size={14} style={{ color: "var(--t-text-faint)", flexShrink: 0 }} />
          <input value={filters.search} onChange={e => setFilters(p => ({ ...p, search: e.target.value }))} aria-label="Search dev tickets"
            placeholder="Search dev tickets, clients, issues…"
            style={{ background: "none", border: "none", color: "var(--t-text-primary)", fontSize: 13, padding: "9px 0", width: "100%", outline: "none", fontFamily: "inherit" }} />
        </div>
        <select aria-label="Status" value={filters.status} onChange={e => setFilters(p => ({ ...p, status: e.target.value }))} style={{ ...inputStyle, cursor: "pointer" }}>
          <option value="">Status: All</option>
          <option value="Active">Active (Open + In Progress + On Hold)</option>
          {["Open", "In Progress", "On Hold", "Resolved", "Closed"].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select aria-label="Priority" value={filters.priority} onChange={e => setFilters(p => ({ ...p, priority: e.target.value }))} style={{ ...inputStyle, cursor: "pointer" }}>
          <option value="">Priority: All</option>
          {["Critical", "High", "Medium", "Low"].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select aria-label="Customer" value={filters.client} onChange={e => setFilters(p => ({ ...p, client: e.target.value }))} style={{ ...inputStyle, cursor: "pointer" }}>
          <option value="">Customer: All</option>
          {clients.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select aria-label="Developer" value={filters.dev} onChange={e => setFilters(p => ({ ...p, dev: e.target.value }))} style={{ ...inputStyle, cursor: "pointer" }}>
          <option value="">Dev: All</option>
          <option value="none">No dev assigned</option>
          {devAgents.map(a => <option key={a.id} value={a.id}>{a.first_name} {a.last_name}</option>)}
        </select>
        {isFiltered && <button type="button" onClick={() => setFilters(DEFAULT_FILTERS)} style={{ ...inputStyle, color: "var(--badge-red)", cursor: "pointer" }}>Clear</button>}
        <button type="button" onClick={load} style={{ ...inputStyle, display: "inline-flex", alignItems: "center", gap: 7, cursor: "pointer", color: "var(--t-text-muted)" }}>
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {error && <div role="alert" style={{ marginBottom: 12, padding: "10px 14px", borderRadius: 8, border: "1px solid rgba(239,68,68,.35)", background: "rgba(239,68,68,.08)", color: "var(--badge-red)", fontSize: 13 }}>{error}</div>}

      {isMobile ? (
        <div style={{ borderTop: "1px solid var(--t-border-soft)" }}>
          {loading && !tickets.length && <div style={{ padding: 40, textAlign: "center", color: "var(--t-text-faint)" }}>Loading…</div>}
          {!loading && shown.length === 0 && <div style={{ padding: 40, textAlign: "center", color: "var(--t-text-faint)", fontSize: 13 }}>No dev tickets match these filters.</div>}
          {shown.map((t, i) => (
            <Fragment key={t.id}>
            {(i === 0 || shown[i - 1].status !== t.status) && <div style={{ ...groupHead, borderRadius: 6, marginTop: i ? 10 : 0 }}>{t.status} · {groupSize[t.status]}</div>}
            <a href={`${base}/${t.id}`} onClick={e => { e.preventDefault(); open(e, t.id); }}
              style={{ display: "block", padding: "12px 2px", borderBottom: "1px solid var(--t-border-soft)", color: "inherit", textDecoration: "none" }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--t-text-primary)" }}>{t.title}</div>
              <div style={{ fontSize: 11, color: "var(--t-text-faint)", marginTop: 2 }}>{fmtTkt(t.ticket_number) || "No source ticket"}{t.client_name ? ` · ${t.client_name}` : ""} · waiting {ago(t.updated_at)}</div>
              <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}><StatusBadge status={t.status} /><PriorityBadge priority={t.priority} /></div>
            </a>
            </Fragment>
          ))}
        </div>
      ) : (
        <div style={{ border: "1px solid var(--t-border-soft)", borderRadius: "var(--t-radius-lg,16px)", background: "var(--t-gloss, none), var(--t-panel)", boxShadow: "var(--t-shadow-panel)", overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 860 }}>
            <thead style={{ background: "var(--t-grad-list-head)" }}>
              <tr>
                {["#", "Subject", "Sent by", "Status", "Priority", "Waiting", "Devs"].map(h => (
                  <th key={h} style={{ padding: "11px 14px", textAlign: "left", fontSize: 10, fontWeight: 700, color: "var(--t-text-faint)", textTransform: "uppercase", letterSpacing: "0.06em", whiteSpace: "nowrap", borderBottom: "1px solid var(--t-border-soft)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && !tickets.length && <tr><td colSpan={7} style={{ padding: 40, textAlign: "center" }}><div className="animate-spin" style={{ width: 24, height: 24, border: "2px solid var(--t-accent)", borderTopColor: "transparent", borderRadius: "50%", margin: "0 auto" }} /></td></tr>}
              {!loading && shown.length === 0 && <tr><td colSpan={7} style={{ padding: 40, textAlign: "center", color: "var(--t-text-faint)", fontSize: 13 }}>No dev tickets match these filters.</td></tr>}
              {shown.map((t, i) => {
                const late = ACTIVE.includes(t.status) && t.status !== "On Hold" && Date.now() - new Date(t.updated_at).getTime() > 2 * 86400000;
                const devs = t.assigned_dev_details || [];
                return (
                  <Fragment key={t.id}>
                  {(i === 0 || shown[i - 1].status !== t.status) && (
                    <tr><td colSpan={7} style={groupHead}>{t.status} · {groupSize[t.status]}</td></tr>
                  )}
                  <tr onClick={e => open(e, t.id)} onAuxClick={e => { if (e.button === 1) { e.preventDefault(); open(e, t.id); } }}
                    style={{ borderBottom: "1px solid var(--t-border-softer)", cursor: "pointer" }}
                    onMouseEnter={e => { e.currentTarget.style.background = "var(--t-accent-softer)"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
                    <td style={{ padding: "12px 14px", fontFamily: "'SF Mono','Fira Code',monospace", fontSize: 11, fontWeight: 600, color: "var(--t-text-faint)", whiteSpace: "nowrap" }}>
                      {fmtTkt(t.ticket_number) || "—"}
                    </td>
                    <td style={{ padding: "12px 14px", maxWidth: 380 }}>
                      <a href={`${base}/${t.id}`} onClick={e => { e.stopPropagation(); if (e.ctrlKey || e.metaKey || e.shiftKey) return; e.preventDefault(); open(e, t.id); }}
                        style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--t-text-primary)", textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.title}</a>
                      <div style={{ fontSize: 11, color: "var(--t-text-faint)", marginTop: 2 }}>{t.client_name || "No client"}{t.type ? ` · ${t.type}` : ""}</div>
                    </td>
                    <td style={{ padding: "12px 14px", whiteSpace: "nowrap" }}>
                      <div style={{ fontSize: 12, color: "var(--t-text-muted)", fontWeight: 500 }}>{t.created_by_name || "—"}</div>
                      {t.ticket_number && <div style={{ fontSize: 11, color: "var(--t-text-faint)", marginTop: 2, display: "inline-flex", alignItems: "center", gap: 4 }}><TicketIcon size={10} /> from {fmtTkt(t.ticket_number)}</div>}
                    </td>
                    <td style={{ padding: "12px 14px" }}><StatusBadge status={t.status} /></td>
                    <td style={{ padding: "12px 14px" }}><PriorityBadge priority={t.priority} /></td>
                    <td style={{ padding: "12px 14px", whiteSpace: "nowrap" }}>
                      <span title={`Last update ${new Date(t.updated_at).toLocaleString()}`} style={{ fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 99,
                        color: t.status === "On Hold" ? "var(--badge-violet)" : late ? "var(--badge-red)" : "var(--badge-green)",
                        background: t.status === "On Hold" ? "rgba(139,92,246,.14)" : late ? "rgba(239,68,68,.13)" : "rgba(52,211,153,.13)" }}>
                        {["Resolved", "Closed"].includes(t.status) ? "Done" : t.status === "On Hold" ? "On hold" : ago(t.updated_at)}
                      </span>
                    </td>
                    <td style={{ padding: "12px 14px", whiteSpace: "nowrap" }}>
                      {devs.length ? (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }} title={devs.map(d => d.name).join(", ")}>
                          <span style={{ display: "inline-flex" }}>{devs.slice(0, 3).map((d, i) => <span key={d.id} style={{ marginLeft: i ? -6 : 0 }}><MiniAvatar name={d.name} size={24} /></span>)}</span>
                          <span style={{ fontSize: 12, color: "var(--t-text-muted)" }}>{devs.length > 2 ? `${devs.length} devs` : devs.map(d => d.name.split(" ")[0]).join(", ")}</span>
                        </span>
                      ) : <span style={{ fontSize: 12, color: "var(--t-text-faint)" }}>Unassigned</span>}
                    </td>
                  </tr>
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
